import { PickerConfig, createPickerConfig } from "shared/components/UniversalTable/types/PickerFieldConfig";

/**
 * Picker configuration for Building Financials table
 *
 * Defines which fields are editable and how they map to picker modals.
 * Only database-sourced fields are editable (not Odoo or calculated fields).
 */
export const BUILDING_FINANCIALS_PICKER_CONFIG: PickerConfig = createPickerConfig({
  entityName: 'gebouw',
  entityPlural: 'gebouwen',
  apiUpdateEndpoint: '/building/{id}/financials',

  fieldMappings: [
    // Acquisition Date (building.acquisitionDate)
    {
      eventMode: 'acquisitionDate',
      pickerMode: 'document-date',
      fieldName: 'acquisitionDate',
      extractCurrentValue: (row) => row.acquisitionDate,
      buildUpdatePayload: (val) => ({ acquisitionDate: val }),
    },

    // Total Bid (buildingAcquisitionInfo.totalBid)
    {
      eventMode: 'totalBid',
      pickerMode: 'number-input',
      fieldName: 'totalBid',
      extractCurrentValue: (row) => row.totalBid,
      buildUpdatePayload: (val) => ({ totalBid: val }),
    },

    // Transaction Costs (buildingAcquisitionInfo.transactionCosts)
    {
      eventMode: 'transactionCosts',
      pickerMode: 'number-input',
      fieldName: 'transactionCosts',
      extractCurrentValue: (row) => row.transactionCosts,
      buildUpdatePayload: (val) => ({ transactionCosts: val }),
    },

    // Broker Costs Net (buildingAcquisitionInfo.brokerCostsNet)
    {
      eventMode: 'brokerCostsNet',
      pickerMode: 'number-input',
      fieldName: 'brokerCostsNet',
      extractCurrentValue: (row) => row.brokerCostsNet,
      buildUpdatePayload: (val) => ({ brokerCostsNet: val }),
    },

    // Due Diligence Cost (buildingAcquisitionInfo.dueDiligenceCost)
    {
      eventMode: 'dueDiligenceCost',
      pickerMode: 'number-input',
      fieldName: 'dueDiligenceCost',
      extractCurrentValue: (row) => row.dueDiligenceCost,
      buildUpdatePayload: (val) => ({ dueDiligenceCost: val }),
    },

    // Fiscal Discount Company Shares (buildingAcquisitionInfo.fiscalDiscountCompanyShares)
    {
      eventMode: 'fiscalDiscountCompanyShares',
      pickerMode: 'number-input',
      fieldName: 'fiscalDiscountCompanyShares',
      extractCurrentValue: (row) => row.fiscalDiscountCompanyShares,
      buildUpdatePayload: (val) => ({ fiscalDiscountCompanyShares: val }),
    },

    // Fiscal Discount Real Estate (buildingAcquisitionInfo.fiscalDiscountRealEstate)
    {
      eventMode: 'fiscalDiscountRealEstate',
      pickerMode: 'number-input',
      fieldName: 'fiscalDiscountRealEstate',
      extractCurrentValue: (row) => row.fiscalDiscountRealEstate,
      buildUpdatePayload: (val) => ({ fiscalDiscountRealEstate: val }),
    },

    // Other Discounts (buildingAcquisitionInfo.otherDiscounts)
    {
      eventMode: 'otherDiscounts',
      pickerMode: 'number-input',
      fieldName: 'otherDiscounts',
      extractCurrentValue: (row) => row.otherDiscounts,
      buildUpdatePayload: (val) => ({ otherDiscounts: val }),
    },

    // Renovation Budget (buildingAcquisitionInfo.renovationBudget)
    {
      eventMode: 'renovationBudget',
      pickerMode: 'number-input',
      fieldName: 'renovationBudget',
      extractCurrentValue: (row) => row.renovationBudget,
      buildUpdatePayload: (val) => ({ renovationBudget: val }),
    },

    // Deferred Registration Taxes (building.backPayments)
    {
      eventMode: 'deferredRegistrationTaxes',
      pickerMode: 'number-input',
      fieldName: 'deferredRegistrationTaxes',
      extractCurrentValue: (row) => row.deferredRegistrationTaxes,
      buildUpdatePayload: (val) => ({ backPayments: val }),
    },

    // Deferred Registration Date (building.deadlineBackPayments)
    {
      eventMode: 'deferredRegistrationDate',
      pickerMode: 'document-date',
      fieldName: 'deferredRegistrationDate',
      extractCurrentValue: (row) => row.deferredRegistrationDate,
      buildUpdatePayload: (val) => ({ deadlineBackPayments: val }),
    },

    // Estimated Market Value (building.estimatedMarketValue) - placeholder
    {
      eventMode: 'estimatedMarketValue',
      pickerMode: 'number-input',
      fieldName: 'estimatedMarketValue',
      extractCurrentValue: (row) => row.estimatedMarketValue,
      buildUpdatePayload: (val) => ({ estimatedMarketValue: val }),
    },

    // Insured Value (building.insuredValue) - placeholder
    {
      eventMode: 'insuredValue',
      pickerMode: 'number-input',
      fieldName: 'insuredValue',
      extractCurrentValue: (row) => row.insuredValue,
      buildUpdatePayload: (val) => ({ insuredValue: val }),
    },
  ],

  fieldLabels: {
    acquisitionDate: 'Aankoopdatum',
    totalBid: 'Vastgoedprijs',
    transactionCosts: 'Transactiekosten',
    brokerCostsNet: 'Makelaarskosten',
    dueDiligenceCost: 'Due diligence kosten',
    fiscalDiscountCompanyShares: 'Fiscale korting vennootschap',
    fiscalDiscountRealEstate: 'Fiscale korting vastgoed',
    otherDiscounts: 'Andere kortingen',
    renovationBudget: 'Renovatiebudget',
    deferredRegistrationTaxes: 'Uitgestelde registratiebelastingen',
    deferredRegistrationDate: 'Deadline nabelasting',
    estimatedMarketValue: 'Geschatte marktwaarde',
    insuredValue: 'Verzekerde waarde',
  },
});
